import {
  Button,
  ConfirmModal,
  Loading,
  notify,
  RowInput,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  AuthService,
  type Server,
  ServersService,
} from '@affine/core/modules/cloud';
import {
  type DialogComponentProps,
  type GLOBAL_DIALOG_SCHEMA,
  GlobalDialogService,
} from '@affine/core/modules/dialogs';
import { FolderTemplateService } from '@affine/core/modules/template-doc';
import { WorkspacesService } from '@affine/core/modules/workspace';
import {
  buildShowcaseWorkspace,
  type WorkspaceProjectData,
} from '@affine/core/utils/first-app-data';
import { useI18n } from '@affine/i18n';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import {
  useWorkspaceTemplates,
  type WorkspaceTemplateOption,
} from '../../../components/create-workspace-menu/use-workspace-templates';
import * as styles from './index.css';

const FormSection = ({
  label,
  input,
}: {
  label: string;
  input: React.ReactNode;
}) => {
  return (
    <section className={styles.section}>
      <label className={styles.label}>{label}</label>
      {input}
    </section>
  );
};

export const CreateWorkspaceDialog = ({
  close,
  ...props
}: DialogComponentProps<GLOBAL_DIALOG_SCHEMA['create-workspace']>) => {
  const t = useI18n();

  const [workspaceName, setWorkspaceName] = useState('');
  const [description, setDescription] = useState('');
  const [client, setClient] = useState('');
  const [deadline, setDeadline] = useState('');
  const [tags, setTags] = useState('');
  const [templateKey, setTemplateKey] = useState<string>('empty');

  // Project type is fixed to OrgHub Cloud (affine-cloud) for all workspaces.
  const serversService = useService(ServersService);
  const server = useLiveData(serversService.server$('affine-cloud'));
  const { options: templateOptions, loading: templatesLoading } =
    useWorkspaceTemplates();

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close();
    },
    [close]
  );

  const projectData: WorkspaceProjectData = {
    description,
    client,
    deadline,
    tags,
  };

  const selectedTemplate =
    templateOptions.find(option => option.key === templateKey) ?? null;

  return (
    <ConfirmModal
      open
      onOpenChange={onOpenChange}
      title={t['com.affine.nameWorkspace.title']()}
      description={t['com.affine.nameWorkspace.description']()}
      cancelText={t['com.affine.nameWorkspace.button.cancel']()}
      closeButtonOptions={{
        ['data-testid' as string]: 'create-workspace-close-button',
      }}
      contentOptions={{}}
      childrenContentClassName={styles.content}
      customConfirmButton={() => {
        return (
          <FrameworkScope scope={server?.scope}>
            <CustomConfirmButton
              workspaceName={workspaceName}
              projectData={projectData}
              server={server}
              template={selectedTemplate}
              onCreated={res =>
                close({ metadata: res.meta, defaultDocId: res.defaultDocId })
              }
            />
          </FrameworkScope>
        );
      }}
      {...props}
    >
      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.workspace-name']()}
        input={
          <RowInput
            autoFocus
            className={styles.input}
            data-testid="create-workspace-input"
            placeholder={t['com.affine.nameWorkspace.placeholder']()}
            maxLength={64}
            minLength={0}
            onChange={setWorkspaceName}
          />
        }
      />

      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.template']()}
        input={
          <div
            className={styles.templateList}
            data-testid="create-workspace-template-list"
          >
            <button
              type="button"
              role="radio"
              aria-checked={templateKey === 'empty'}
              className={
                templateKey === 'empty'
                  ? styles.templateOptionActive
                  : styles.templateOption
              }
              onClick={() => setTemplateKey('empty')}
            >
              <span
                className={
                  templateKey === 'empty'
                    ? styles.templateDotActive
                    : styles.templateDot
                }
              />
              <span className={styles.templateTitle}>
                {t['com.affine.nameWorkspace.template.empty']()}
              </span>
            </button>
            {templatesLoading ? (
              <div className={styles.templateHint}>
                <Loading size={16} speed={0} />
                {t['com.affine.nameWorkspace.template.loading']()}
              </div>
            ) : (
              <>
                {templateOptions.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={templateKey === option.key}
                    className={
                      templateKey === option.key
                        ? styles.templateOptionActive
                        : styles.templateOption
                    }
                    onClick={() => setTemplateKey(option.key)}
                    data-testid={`create-workspace-template-${option.folderId}`}
                  >
                    <span
                      className={
                        templateKey === option.key
                          ? styles.templateDotActive
                          : styles.templateDot
                      }
                    />
                    <span className={styles.templateTitles}>
                      <span className={styles.templateTitle}>
                        {option.folderName}
                      </span>
                      <span className={styles.templateSubtitle}>
                        {option.workspaceName}
                      </span>
                    </span>
                  </button>
                ))}
                {templateOptions.length === 0 && (
                  <div className={styles.templateHint}>
                    {t['com.affine.nameWorkspace.template.none']()}
                  </div>
                )}
              </>
            )}
          </div>
        }
      />

      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.description']()}
        input={
          <textarea
            className={styles.textarea}
            data-testid="create-workspace-description-input"
            placeholder={t[
              'com.affine.nameWorkspace.placeholder.description'
            ]()}
            maxLength={2000}
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        }
      />

      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.client']()}
        input={
          <RowInput
            className={styles.input}
            data-testid="create-workspace-client-input"
            placeholder={t['com.affine.nameWorkspace.placeholder.client']()}
            maxLength={128}
            onChange={setClient}
          />
        }
      />

      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.deadline']()}
        input={
          <RowInput
            type="date"
            className={styles.input}
            data-testid="create-workspace-deadline-input"
            onChange={setDeadline}
          />
        }
      />

      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.tags']()}
        input={
          <RowInput
            className={styles.input}
            data-testid="create-workspace-tags-input"
            placeholder={t['com.affine.nameWorkspace.placeholder.tags']()}
            maxLength={256}
            onChange={setTags}
          />
        }
      />
    </ConfirmModal>
  );
};

const CustomConfirmButton = ({
  workspaceName,
  projectData,
  server,
  template,
  onCreated,
}: {
  workspaceName: string;
  projectData?: WorkspaceProjectData;
  server?: Server | null;
  template: WorkspaceTemplateOption | null;
  onCreated: (res: Awaited<ReturnType<typeof buildShowcaseWorkspace>>) => void;
}) => {
  const t = useI18n();
  const [loading, setLoading] = useState(false);

  const session = useService(AuthService).session;
  const loginStatus = useLiveData(session.status$);
  const globalDialogService = useService(GlobalDialogService);
  const workspacesService = useService(WorkspacesService);

  const openSignInModal = useCallback(() => {
    globalDialogService.open('sign-in', { server: server?.baseUrl });
  }, [globalDialogService, server?.baseUrl]);

  const handleConfirm = useAsyncCallback(async () => {
    if (loading) return;
    setLoading(true);

    // this will be the last step for web for now
    // fix me later
    try {
      if (template) {
        // seed the new workspace from a folder template
        const opened = workspacesService.openByWorkspaceId(
          template.workspaceId
        );
        if (!opened) {
          throw new Error(
            `Source workspace ${template.workspaceId} is not available`
          );
        }
        let metadata;
        try {
          await opened.workspace.engine.doc.waitForDocReady(
            template.workspaceId
          );
          const folderTemplateService = opened.workspace.scope.get(
            FolderTemplateService
          );
          metadata = await folderTemplateService.instantiateAsWorkspace(
            template.folderId,
            workspaceName,
            server?.id ?? 'local',
            projectData
          );
        } finally {
          opened.dispose();
        }
        onCreated({ meta: metadata, defaultDocId: undefined });
      } else {
        const res = await buildShowcaseWorkspace(
          workspacesService,
          server?.id ?? 'local',
          workspaceName,
          projectData
        );
        onCreated(res);
      }
    } catch (e) {
      console.error(e);
      notify.error({
        title: t['com.affine.workspace.failed-to-create'](),
        message: t['com.affine.please-try-again-later'](),
      });
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    onCreated,
    projectData,
    server,
    template,
    workspaceName,
    workspacesService,
  ]);

  const handleCheckSessionAndConfirm = useCallback(() => {
    if (server && loginStatus !== 'authenticated') {
      return openSignInModal();
    }
    handleConfirm();
  }, [handleConfirm, loginStatus, openSignInModal, server]);

  return (
    <Button
      disabled={!workspaceName}
      data-testid="create-workspace-create-button"
      variant="primary"
      onClick={handleCheckSessionAndConfirm}
      loading={loading}
    >
      {t['com.affine.nameWorkspace.button.create']()}
    </Button>
  );
};
